const WISHLIST_STORAGE_KEY = 'honydress:wishlist';

class WishlistStorage {
  static getItems() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  static saveItems(items) {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    WishlistStorage.updateBubble();
    publish(PUB_SUB_EVENTS.wishlistUpdate, { items });
  }

  static add(handle, variantId) {
    const items = this.getItems();
    if (items.some((item) => item.handle === handle)) return false;
    items.push({ handle, variantId: Number(variantId) });
    this.saveItems(items);
    return true;
  }

  static remove(handle) {
    this.saveItems(this.getItems().filter((item) => item.handle !== handle));
  }

  static toggle(handle, variantId) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle, variantId);
    return true;
  }

  static has(handle) {
    return this.getItems().some((item) => item.handle === handle);
  }

  static count() {
    return this.getItems().length;
  }

  static updateVariant(handle, variantId) {
    const items = this.getItems();
    const item = items.find((entry) => entry.handle === handle);
    if (!item) return;
    item.variantId = Number(variantId);
    this.saveItems(items);
  }

  static updateBubble() {
    const bubble = document.getElementById('wishlist-icon-bubble');
    if (!bubble) return;

    const count = this.count();
    if (count === 0) {
      bubble.replaceChildren();
      return;
    }

    let countEl = bubble.querySelector('[aria-hidden="true"]');
    if (!countEl) {
      countEl = document.createElement('span');
      countEl.setAttribute('aria-hidden', 'true');
      bubble.appendChild(countEl);
    }
    countEl.textContent = count < 100 ? count : '';
  }
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.handle;
    this.sectionId = this.dataset.sectionId;

    this.button?.addEventListener('click', this.onClick.bind(this));
    this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data.sectionId !== this.sectionId) return;
      this.dataset.variantId = event.data.variant.id;
      if (WishlistStorage.has(this.handle)) {
        WishlistStorage.updateVariant(this.handle, event.data.variant.id);
      }
      this.updateState();
    });
    this.wishlistUnsubscriber = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.updateState());
    this.updateState();
  }

  disconnectedCallback() {
    this.variantUnsubscriber?.();
    this.wishlistUnsubscriber?.();
  }

  onClick() {
    WishlistStorage.toggle(this.handle, this.dataset.variantId);
    this.updateState();
  }

  updateState() {
    const active = WishlistStorage.has(this.handle);
    this.button?.classList.toggle('wishlist-button__toggle--active', active);
    const label = this.querySelector('.wishlist-button__label');
    if (label) {
      label.textContent = active ? label.dataset.removeLabel : label.dataset.addLabel;
    }
    this.button?.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
}

class WishlistItems extends HTMLElement {
  connectedCallback() {
    this.emptyEl = this.querySelector('.wishlist__empty');
    this.gridEl = this.querySelector('.wishlist__grid');
    this.render();
    this.unsubscriber = subscribe(PUB_SUB_EVENTS.wishlistUpdate, () => this.render());
  }

  disconnectedCallback() {
    this.unsubscriber?.();
  }

  async render() {
    const items = WishlistStorage.getItems();
    WishlistStorage.updateBubble();

    if (!this.gridEl || !this.emptyEl) return;

    if (items.length === 0) {
      this.emptyEl.classList.remove('hidden');
      this.gridEl.classList.add('hidden');
      this.gridEl.replaceChildren();
      return;
    }

    this.emptyEl.classList.add('hidden');
    this.gridEl.classList.remove('hidden');
    this.gridEl.replaceChildren();

    const results = await Promise.all(
      items.map(async (item) => {
        try {
          const response = await fetch(`${window.shopUrl}/products/${item.handle}.js`);
          if (!response.ok) return { item, missing: true };
          const product = await response.json();
          const variant =
            product.variants.find((entry) => entry.id === item.variantId) || product.variants[0];
          return { item, product, variant };
        } catch {
          return { item, missing: true };
        }
      })
    );

    results.forEach((result) => {
      if (result.missing) {
        WishlistStorage.remove(result.item.handle);
        return;
      }
      this.gridEl.appendChild(this.createItemElement(result.product, result.variant));
    });
  }

  createItemElement(product, variant) {
    const item = document.createElement('div');
    item.className = 'wishlist-item';

    const imageUrl = product.featured_image
      ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=300`
      : '';
    const price = this.formatMoney(variant.price);
    const comparePrice =
      variant.compare_at_price > variant.price ? this.formatMoney(variant.compare_at_price) : '';

    item.innerHTML = `
      <div class="wishlist-item__inner">
        <a href="${product.url}" class="wishlist-item__media">
          ${
            imageUrl
              ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="300" height="300">`
              : ''
          }
        </a>
        <div class="wishlist-item__content">
          <a href="${product.url}" class="wishlist-item__title h4">${this.escapeHtml(product.title)}</a>
          <div class="wishlist-item__price price${comparePrice ? ' price--on-sale' : ''}">
            ${comparePrice ? `<s class="price-item price-item--regular">${comparePrice}</s>` : ''}
            <span class="price-item price-item--${comparePrice ? 'sale' : 'regular'}">${price}</span>
          </div>
          <div class="wishlist-item__actions">
            <button type="button" class="button button--primary wishlist-item__add" ${
              variant.available ? '' : 'disabled'
            } data-variant-id="${variant.id}">
              ${variant.available ? window.wishlistStrings.addToCart : window.wishlistStrings.soldOut}
            </button>
            <button type="button" class="button button--secondary wishlist-item__remove" data-handle="${product.handle}">
              ${window.wishlistStrings.remove}
            </button>
          </div>
        </div>
      </div>
    `;

    item.querySelector('.wishlist-item__remove')?.addEventListener('click', () => {
      WishlistStorage.remove(product.handle);
    });

    item.querySelector('.wishlist-item__add')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      button.setAttribute('disabled', 'disabled');
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
        });
        if (response.ok) {
          publish(PUB_SUB_EVENTS.cartUpdate, {});
        } else {
          button.removeAttribute('disabled');
        }
      } catch {
        button.removeAttribute('disabled');
      }
    });

    return item;
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  }

  escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
if (!customElements.get('wishlist-items')) {
  customElements.define('wishlist-items', WishlistItems);
}

document.addEventListener('DOMContentLoaded', () => WishlistStorage.updateBubble());
